
import Vue from "vue";
import Component from "vue-class-component";
import HeadingWave from "@/features/shared/components/HeadingWave.vue";
import HeadingBlue from "@/features/shared/components/HeadingBlue.vue";
import HeadingGroup from "@/features/shared/components/HeadingGroup.vue";
import {
    CertificateDetailsModel,
    MinimalDocumentModel,
    DocumentValidityClient,
    DocumentValidityModel,
    EndorsementDetailsModel,
    factory,
    ResultError,
    GetMedicalCertificateResponse, CertificatesClient, EndorsementsClient, ApiException,
} from "@/api-client";
import LoadingSpinner from "@/components/LoadingSpinner.vue";
import Loader from "@/features/shared/components/Loader";
import EndorsementDetailsContent from "@/features/endorsements/EndorsementDetailsContent.vue";
import CertificateDetailsContent from "@/features/certificates/CertificateDetailsContent.vue";
import DocumentValidity from "@/features/documents/query/components/DocumentValidity.vue";
import SectionPageHeading from "@/features/shared/components/sections/SectionPageHeading.vue";
import Captcha from "@/features/shared/components/Captcha.vue";
import MedicalCertificateDetailsContent from "@/features/medicalCertificates/components/MedicalCertificateDetailsContent.vue";
import MedicalCertificateResponse from "@/features/documents/query/components/MedicalCertificateResponse.vue";
import MedicalCertificate from "@/features/documents/query/components/MedicalCertificate.vue";
import MinimalMedicalCertificate from "@/features/documents/query/components/MinimalMedicalCertificate.vue";
import DocumentDigitalRepresentationModal from "@/features/shared/components/DocumentDigitalRepresentationModal.vue";
import renderCertificatePrintTemplate from "@/features/shared/CertificatePrintService";
import renderEndorsementPrintTemplate from "@/features/shared/EndorsementPrintService";
import QrCode from "@/components/QrCode.vue";
import {ls} from "@/CultureService";
import DocumentValidityQueryTimestamp from "@/features/shared/components/DocumentValidityQueryTimestamp.vue";
import toasts from "@/components/Toasts";

@Component({
    components: {
        QrCode,
        Captcha,
        DocumentValidity,
        SectionPageHeading,
        HeadingWave,
        HeadingBlue,
        LoadingSpinner,
        EndorsementDetailsContent,
        CertificateDetailsContent,
        MedicalCertificateDetailsContent,
        MedicalCertificateResponse,
        MedicalCertificate,
        HeadingGroup,
        DocumentDigitalRepresentationModal,
        DocumentValidityQueryTimestamp,
        MinimalMedicalCertificate
    },
})
export default class DocumentValidityQuery extends Vue {
    personalCode = "";
    firstName = "";
    lastName = "";
    documentNumber = "";
    birthDate: Date|null = null;
    accessToken = "";

    errors = factory.create(ResultError);

    document: DocumentValidityModel | null = null;
    documentList: DocumentValidityModel[] | null = null;
    documentDetails:
        | MinimalDocumentModel
        | EndorsementDetailsModel
        | CertificateDetailsModel
        | GetMedicalCertificateResponse
        | null = null;
    loader = new Loader();

    $refs!: {
        captcha: Captcha;
        digitalRepresentationModal: DocumentDigitalRepresentationModal;
        certificateQrCode: QrCode;
        endorsementQrCode: QrCode;
    };

    get isMinimalDocument(): boolean {
        return !(this.documentDetails instanceof CertificateDetailsModel) && !(this.documentDetails instanceof EndorsementDetailsModel);
    }

    async searchByDocumentNumber() {
        this.errors.clear();
        this.document = null;
        this.documentDetails = null;
        this.documentList = null;

        await new DocumentValidityClient()
            .setLoader(this.loader)
            .byDocumentNumber(
                this.personalCode.trim(),
                this.firstName.trim(),
                this.lastName.trim(),
                this.documentNumber.trim(),
                await this.$refs.captcha.getCaptchaToken("document_validity_by_document_number")
            )
            .then((result) => {
                this.document = result;
                toasts.success(this.$ls.Global_Toast_QuerySuccess);
            }).catch((error) => this.handleErrors(error))
    }

    async searchByBirthDate() {
        this.errors.clear();
        this.document = null;
        this.documentDetails = null;
        const utcBirthDate = this.birthDate
            ? new Date(Date.UTC(this.birthDate.getFullYear(), this.birthDate.getMonth(), this.birthDate.getDate()))
            : null;
        await new DocumentValidityClient()
            .setLoader(this.loader)
            .byBirthdate(
                this.firstName.trim(),
                this.lastName.trim(),
                utcBirthDate,
                await this.$refs.captcha.getCaptchaToken("document_validity_by_birthdate")
            )
            .then((result) => {
                this.documentList = result;
                toasts.success(this.$ls.Global_Toast_QuerySuccess);
            }).catch((error) => this.handleErrors(error))
    }

    async searchByAccessToken() {
        this.errors.clear();
        this.document = null;
        this.documentDetails = null;
        this.documentList =null
        await new DocumentValidityClient()
            .setLoader(this.loader)
            .byAccessToken(
                this.accessToken.trim(),
                await this.$refs.captcha.getCaptchaToken("document_validity_by_access_token")
            )
            .then((result) => {
                (this.documentDetails = result);
                toasts.success(this.$ls.Global_Toast_QuerySuccess);
            }).catch((error) => this.handleErrors(error));
    }

    get isEndorsement(): boolean {
        return this.documentDetails instanceof EndorsementDetailsModel;
    }


    get componentName() {
        if (this.isEndorsement)
            return "EndorsementDetailsContent";
        if (this.documentDetails instanceof CertificateDetailsModel)
            return "CertificateDetailsContent";
        if (this.documentDetails instanceof GetMedicalCertificateResponse)
            return "MedicalCertificateResponse";
        if (this.documentDetails instanceof MinimalDocumentModel) return "DocumentValidity";
        return "";
    }

    get certificateAccessTokenUrl() {
        if (!this.documentDetails)
            return "";

        return this.documentDetails instanceof CertificateDetailsModel
            ? (this.documentDetails as CertificateDetailsModel).accessTokenUrl
            : "";
    }

    get endorsementAccessTokenUrl() {
        if (!this.documentDetails)
            return "";

        return this.documentDetails instanceof EndorsementDetailsModel
            ? (this.documentDetails as EndorsementDetailsModel).accessTokenUrl
            : "";
    }

    resetParameters() {
        this.errors.clear();
        this.firstName = "";
        this.lastName = "";
        this.personalCode = "";
        this.accessToken = "";
        this.documentNumber = "";
    }

    handleErrors(error: ResultError | ApiException): void {
        this.errors.clear();
        if (error instanceof ResultError) {
            this.setValidationErrors(error, this.$ls.Global_Toast_FormQueryFailure, "errors");
        }
        else if (error.status === 404) {
            const notFoundError = factory.create(ResultError);
            notFoundError.appendMessage(ls.Documents_Query_Validation_NotFound);
            this.setValidationErrors(notFoundError, null, "errors");
        }
        else
            throw error;
    }

    async openDigitalRepresentation() {
        if (this.documentDetails instanceof CertificateDetailsModel) {
            const printTemplate = (await new CertificatesClient().printTemplateByAccessToken(this.accessToken));
            const renderedPrintTemplate = await renderCertificatePrintTemplate(this.documentDetails as CertificateDetailsModel, printTemplate.url, this.$refs.certificateQrCode.getBase64QrCode(), printTemplate.backUrl, printTemplate.backUrl, {transparentSignatureUrl: printTemplate.transparentSignatureUrl, transparentOfficialSignatureUrl: printTemplate.transparentOfficialSignatureUrl});
            return this.$refs.digitalRepresentationModal.show(renderedPrintTemplate);
        }

        if (this.documentDetails instanceof EndorsementDetailsModel) {
            const printTemplate = (await new EndorsementsClient().printTemplateByAccessToken(this.accessToken));
            const renderedPrintTemplate = await renderEndorsementPrintTemplate(this.documentDetails as EndorsementDetailsModel, printTemplate.url, this.$refs.endorsementQrCode.getBase64QrCode(), printTemplate.frontUrl, printTemplate.backUrl, {transparentSignatureUrl: printTemplate.transparentSignatureUrl, transparentOfficialSignatureUrl: printTemplate.transparentOfficialSignatureUrl});
            return this.$refs.digitalRepresentationModal.show(renderedPrintTemplate);
        }

        throw Error("There is no digital representation for given document.");
    }
}
